<template>
    <ResourcePage v-if="article" :theme="theme">
        <template #header>
            <ResourcePageHero :document="article" />
        </template>
        <template #main>
            <SliceZone :components="components" :slices="articleDocument?.data?.slices" />
        </template>
        <template #aside>
            <TableOfContents :document="article" />
            <div v-if="breakpoints?.isMobile" :class="{'article-mobile-table-of-contents-sticky': true, hidden: !showSticky}">
                <TableOfContents :document="article" :collapsible="true" />
            </div>
        </template>
    </ResourcePage>
</template>

<script lang="ts" setup>
    import {computed, inject, onBeforeUnmount, onMounted, ref} from 'vue';
    import {SliceZone} from '@prismicio/vue';
    import type {ArticleDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {useAsyncData, useWebsitePageView} from '#imports';
    import {MEDIA_BREAKPOINTS, PRISMIC_DOCUMENT} from '~/app/keys';
    import ResourcePage from '~/components/ResourcePage.vue';
    import ResourcePageHero from '~/components/ResourcePageHero.vue';
    import TableOfContents from '~/components/TableOfContents.vue';
    import usePrismicDocument from '~/composables/content/usePrismicDocument';
    import {components} from '~/slices/components';

    const {
        params: {uid},
    } = useRoute('resources-article-uid');

    const articleDocument = inject(PRISMIC_DOCUMENT, ref());
    const breakpoints = inject(MEDIA_BREAKPOINTS);
    const showSticky = ref(false);
    const {data: article} = await useAsyncData('article', () => usePrismicDocument<ArticleDocument>('article', uid as string), {deep: true});
    articleDocument.value = article.value;

    const handleScroll = () => {
        const topMenu = document.getElementById('top-menu');
        if (topMenu && topMenu?.getBoundingClientRect().top < 76) {
            showSticky.value = true;
        } else {
            showSticky.value = false;
        }
    };

    const theme = computed(() => {
        if (article.value?.data.theme) {
            return articleDocument.value?.data.theme;
        }
        if (article.value?.data.useAlternativeHeroShapes) {
            return 'corporate';
        }
        return 'default';
    });

    onMounted(() => {
        handleScroll();
        window.addEventListener('scroll', handleScroll);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScroll);
    });

    useWebsitePageView('resources-center', 'articles');
</script>

<style lang="scss">
    .article-mobile-table-of-contents-sticky {
        position: fixed;
        z-index: 9999;
        bottom: 0;
        left: 0;
        right: 0;
        transition: transform var(--joy-transition-duration-default) var(--joy-transition-timing-function);
        transform: translateY(0%);
        &.hidden {
            transform: translateY(100%);
        }
    }
</style>
